<script>
    import { getContext, onMount, onDestroy } from "svelte/internal";

    import SidebarTab from "./SidebarTab.svelte";

    import { user, isUserStayOnTab } from "../../../stores";

    import ExpandableTab from "./ExpandableTab.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        CheckFollowUpItemInterval,
        exportIcon,
    } from "../../../Utils/utilConstants.svelte";

    import { checkEnvIsGroup, checkIfShowTab } from "../../../Utils/utilRoute.svelte";

    import { showException } from "../../../Utils/utilFunctions.svelte";

    export let sidebarExpanded;

    let followUpNumbers = {
        consentDue: 0,
        patientPaymentsDue: 0,
        moreThan30Days: 0,
        claimError: 0,
        underPaid: 0,
        overPaid: 0,
    };

    let checkFollowUpItemInterval;

    $: if (!$isUserStayOnTab) stopWatchingFollowUpItems();
    else startWatchingFollowUpItems();

    onDestroy(() => {
        stopWatchingFollowUpItems();
    });

    const startWatchingFollowUpItems = () => {
        stopWatchingFollowUpItems();
        checkFollowUpItems();
        checkFollowUpItemInterval = setInterval(() => {
            checkFollowUpItems();
        }, CheckFollowUpItemInterval);
    };

    const stopWatchingFollowUpItems = () => {
        clearInterval(checkFollowUpItemInterval);
    };

    const checkFollowUpItems = () => {
        apiMethods.getMethod
            .getNumberOfFollowUpItems()
            .then((res) => handleFollowUpItemCounts(res))
            .catch((err) => showException(err));
    };

    const handleFollowUpItemCounts = (res) => {
        followUpNumbers = res;
    };

    $: totalFollowUp = countTotal(followUpNumbers);

    const countTotal = (obj) => {
        if (obj && typeof obj === "object")
            return Object.values(obj).reduce((sum, val) => sum + val, 0);
        else return 0;
    };

    const stateContext = getContext("sidebarState");

    const toggleSidebarExpanded = () => {
        stateContext.setSidebarExpanded();
    };

    // HELPER FUNCTIONS
    const _checkIfShowCollapseTab = (pathArr = []) => {
        if (!pathArr.length) return false;

        return !pathArr.every((path) => !checkIfShowTab($user.role, path));
    };
</script>

<div
    class="flex flex-0 flex-col shadow-xl shadow-slate-400 overflow-y-auto overflow-x-hidden bg-white"
>
    <span
        class={`collapse-icon flex ${
            sidebarExpanded ? "justify-end mr-3" : "justify-center"
        }`}
        on:click|preventDefault={toggleSidebarExpanded}
    >
        <i
            style="font-size:24px"
            class={`fa cursor-pointer rounded-lg p-3 text-[#6F726C] ${
                sidebarExpanded ? "rotate-back" : "rotate-180"
            }`}
        >
            &#xf104;
        </i>
    </span>

    <div
        class={`sidebar flex flex-1 flex-col relative items-left justify-between ${
            sidebarExpanded ? "pl-3 w-60" : "w-20"
        }`}
    >
        <div
            class="flex flex-col absolute overflow-y-auto overflow-x-hidden inset-0"
        >
            <ul class={`menu pr-2 py-2 pt-0 ${sidebarExpanded ? "w-54" : ""}`}>
                <ExpandableTab
                    title="Admin"
                    iFont="fa-solid fa-user-tie"
                    {sidebarExpanded}
                    isShow={$user.role == 100 || $user.role == 20}
                    routesArr={["ExternalGroups", "Groups", "GroupInvoices"]}
                >
                    <SidebarTab
                        route="ExternalGroups"
                        title="External Groups"
                        iFont="fa-solid fa-users-rays"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "ExternalGroups")}
                    />

                    <SidebarTab
                        route="Groups"
                        title="Groups"
                        iFont="fa-solid fa-users-viewfinder fa-sm"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Groups")}
                    />

                    <SidebarTab
                        route="GroupInvoices"
                        title="Group Invoices"
                        iFont="fa fa-file-invoice-dollar"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "GroupInvoices")}
                    />
                </ExpandableTab>

                <ExpandableTab
                    title="Doctors"
                    iFont="fa fa-user-doctor"
                    {sidebarExpanded}
                    isShow={_checkIfShowCollapseTab([
                        "Account",
                        "AccountSchedules",
                        "AccountUsage",
                        "AccountInvoice",
                    ])}
                    routesArr={[
                        "Account",
                        "AccountSchedules",
                        "AccountUsage",
                        "AccountInvoice",
                    ]}
                >
                    <SidebarTab
                        route="Account"
                        title="Doctors"
                        iFont="fa fa-user-doctor"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Account")}
                    />

                    <SidebarTab
                        route="AccountSchedules"
                        title="Lists"
                        iFont="fa-solid fa-list-ol"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "AccountSchedules")}
                    />

                    <SidebarTab
                        route="AccountUsage"
                        title="Usage"
                        iFont="fa fa-notes-medical"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "AccountUsage")}
                    />

                    <SidebarTab
                        route="AccountInvoice"
                        title="Invoices"
                        iFont="fa fa-file-invoice-dollar"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "AccountInvoice")}
                    />
                </ExpandableTab>

                <SidebarTab
                    route="Patient"
                    title="Patients"
                    iFont="fa fa-user-injured"
                    {sidebarExpanded}
                    isShow={checkIfShowTab($user.role, "Patient")}
                />

                <SidebarTab
                    route="PatientEncounter"
                    title="Patient Encounters"
                    iFont="fa fa-hand-holding-medical"
                    {sidebarExpanded}
                    isShow={checkIfShowTab($user.role, "PatientEncounter")}
                />

                <SidebarTab
                    route="AccountSchedules"
                    title="Lists"
                    iFont="fa-solid fa-list-ol"
                    {sidebarExpanded}
                    isShow={$user.role == 2}
                />

                <SidebarTab
                    route="PatientQuote"
                    title="Fee Estimates"
                    iFont="fa-solid fa-person-circle-question"
                    {sidebarExpanded}
                    isShow={checkIfShowTab($user.role, "PatientQuote")}
                />

                <SidebarTab
                    route="DailyPlanner"
                    title="Daily Planner"
                    iFont="fa-solid fa-calendar-day"
                    {sidebarExpanded}
                    isShow={checkIfShowTab($user.role, "DailyPlanner")}
                />

                <SidebarTab
                    route="Calendar"
                    title="Calendar"
                    iFont="fa-solid fa-calendar-days"
                    {sidebarExpanded}
                    isShow={checkIfShowTab($user.role, "Calendar")}
                />

                <ExpandableTab
                    title={"Follow Up (" + totalFollowUp + ")"}
                    iFont="fa-solid fa-clock"
                    {sidebarExpanded}
                    isShow={_checkIfShowCollapseTab([
                        "InProgress",
                        "ConsentDue",
                        "PatientPaymentDue",
                        "FollowUp",
                        "ClaimError",
                        "Underpaid",
                        "Overpaid",
                        "PendingRefund",
                        "PendingReview",
                        "OnHoldAndRemoved",
                        "DebtCollection",
                        "UnsentDocuments"
                    ])}
                    routesArr={[
                        "InProgress",
                        "ConsentDue",
                        "PatientPaymentDue",
                        "FollowUp",
                        "ClaimError",
                        "Underpaid",
                        "Overpaid",
                        "PendingRefund",
                        "PendingReview",
                        "OnHoldAndRemoved",
                        "DebtCollection",
                        "UnsentDocuments"
                    ]}
                >
                    <SidebarTab
                        route="ConsentDue"
                        title={"Consent Due (" +
                            (followUpNumbers.consentDue ?? 0) +
                            ")"}
                        iFont="fa-solid fa-check-double"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "ConsentDue")}
                    />

                    <SidebarTab
                        route="PatientPaymentDue"
                        title={"Patient Payment Due (" +
                            (followUpNumbers.patientPaymentsDue ?? 0) +
                            ")"}
                        iFont="fa-solid fa-hourglass-end"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "PatientPaymentDue")}
                    />

                    <SidebarTab
                        route="PendingRefund"
                        title={"Pending Refund (" +
                            (followUpNumbers.pendingRefund ?? 0) +
                            ")"}
                        iFont="fa-solid fa-stopwatch-20"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "PatientPaymentDue")}
                    />

                    <SidebarTab
                        route="PendingReview"
                        title={"Pending Review (" +
                            (followUpNumbers.pendingReview ?? 0) +
                            ")"}
                        iFont="fa-solid fa-laptop-file"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "PendingReview")}
                    />

                    <SidebarTab
                        route="DebtCollection"
                        title={"Debt Collection (" +
                            (followUpNumbers.debtCollection ?? 0) +
                            ")"}
                        iFont="fa-regular fa-credit-card"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "DebtCollection")}
                    />

                    <SidebarTab
                        route="OnHoldAndRemoved"
                        title={"On hold & Pending Cancellation (" +
                            (followUpNumbers.pendingCancellationAndOnHold ??
                                0) +
                            ")"}
                        iFont="fa-solid fa-spinner"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "OnHoldAndRemoved")}
                    />

                    <SidebarTab
                        route="FollowUp"
                        title={"> 30 Days (" +
                            (followUpNumbers.moreThan30Days ?? 0) +
                            ")"}
                        iFont="fa-solid fa-clock-rotate-left"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "FollowUp")}
                    />

                    <SidebarTab
                        route="ClaimError"
                        title={"Claim Errors (" +
                            (followUpNumbers.claimError ?? 0) +
                            ")"}
                        iFont="fa-solid fa-file-circle-xmark"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "ClaimError")}
                    />

                    <SidebarTab
                        route="Underpaid"
                        title={"Underpaid (" +
                            (followUpNumbers.underPaid ?? 0) +
                            ")"}
                        iFont="fa-solid fa-scale-unbalanced"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Underpaid")}
                    />

                    <SidebarTab
                        route="Overpaid"
                        title={"OverPaid (" +
                            (followUpNumbers.overPaid ?? 0) +
                            ")"}
                        iFont="fa-solid fa-scale-unbalanced-flip"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Overpaid")}
                    />

                    <SidebarTab
                        route="UnsentDocuments"
                        title={`Unsent Docs (${(followUpNumbers.unsentDocuments ?? 0)})`}
                        iFont="fa-solid fa-file"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "UnsentDocuments")}
                    />
                    
                </ExpandableTab>

                <ExpandableTab
                    title="Billing"
                    iFont="fa fa-receipt"
                    {sidebarExpanded}
                    isShow={_checkIfShowCollapseTab([
                        "Estimates",
                        "Invoices",
                        "Receipts",
                        "Reconciliations",
                        "BillingRules",
                        "AccountPresets",
                        "ThirdPartyInsurer",
                    ])}
                    routesArr={[
                        "Estimates",
                        "Invoices",
                        "Receipts",
                        "Reconciliations",
                        "BillingRules",
                        "AccountPresets",
                        "ThirdPartyInsurer",
                    ]}
                >
                    <SidebarTab
                        route="Estimates"
                        title="Quotes"
                        iFont="fa fa-file-invoice"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Estimates")}
                    />

                    <SidebarTab
                        route="Invoices"
                        title="Invoices"
                        iFont="fa fa-file-invoice-dollar"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Invoices")}
                    />

                    <SidebarTab
                        route="Receipts"
                        title="Payments"
                        iFont="fa fa-receipt"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Receipts")}
                    />

                    <SidebarTab
                        route="Reconciliations"
                        title="Reconciliation"
                        iFont="fa-solid fa-list-check"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Reconciliations")}
                    />

                    <SidebarTab
                        route="BillingRules"
                        title="Billing Rules"
                        iFont="fa-solid fa-clipboard-list"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "BillingRules")}
                    />

                    <SidebarTab
                        route="AccountPresets"
                        title="Presets"
                        iFont="fa-solid fa-file-signature"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "AccountPresets")}
                    />

                    <SidebarTab
                        route="ThirdPartyInsurer"
                        title="Third-party Insurer"
                        iFont="fa-solid fa-clover"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "ThirdPartyInsurer")}
                    />
                </ExpandableTab>

                <SidebarTab
                    route="MyInvoices"
                    title="My Invoices"
                    iFont="fa-solid fa-file-invoice-dollar"
                    {sidebarExpanded}
                    isShow={checkIfShowTab($user.role, "MyInvoice")}
                />

                <SidebarTab
                    route="Reports"
                    title="Reports"
                    iFont={exportIcon}
                    {sidebarExpanded}
                    isShow={checkIfShowTab($user.role, "Reports")}
                />

                <ExpandableTab
                    title="Medical Data"
                    iFont="fa fa-house-medical"
                    {sidebarExpanded}
                    isShow={_checkIfShowCollapseTab([
                        "SurgeryRooms",
                        "AllProviders",
                        "Hospitals",
                        "BupaPracticeIds",
                        "InsuranceFunds",
                        "WorkCoverAuthorities",
                        "TrafficAccidentAuthorities",
                        "MBSItems",
                        "ASAItems",
                    ])}
                    routesArr={[
                        "SurgeryRooms",
                        "AllProviders",
                        "Hospitals",
                        "BupaPracticeIds",
                        "InsuranceFunds",
                        "WorkCoverAuthorities",
                        "TrafficAccidentAuthorities",
                        "MBSItems",
                        "ASAItems",
                    ]}
                >
                    <SidebarTab
                        route="SurgeryRooms"
                        title="Surgery Rooms"
                        iFont="fa-solid fa-person-booth"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "SurgeryRooms")}
                    />

                    <SidebarTab
                        route="AllProviders"
                        title="All Providers"
                        iFont="fa fa-user-nurse"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "AllProviders")}
                    />

                    <SidebarTab
                        route="Hospitals"
                        title="Hospitals"
                        iFont="fa fa-house-medical"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Hospitals")}
                    />

                    <SidebarTab
                        route="InsuranceFunds"
                        title="Insurance Funds"
                        iFont="fa fa-hospital"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "InsuranceFunds")}
                    />

                    <SidebarTab
                        route="BupaPracticeIds"
                        title="Bupa Practice IDs"
                        iFont="fa-solid fa-heart-pulse"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "BupaPracticeIds")}
                    />

                    <SidebarTab
                        route="WorkCoverAuthorities"
                        title="Work Cover Authorities"
                        iFont="fa-solid fa-helmet-safety"
                        {sidebarExpanded}
                        isShow={checkIfShowTab(
                            $user.role,
                            "WorkCoverAuthorities",
                        )}
                    />

                    <SidebarTab
                        route="TrafficAccidentAuthorities"
                        title="Traffic Accident Authorities"
                        iFont="fas fa-car-crash"
                        {sidebarExpanded}
                        isShow={checkIfShowTab(
                            $user.role,
                            "TrafficAccidentAuthorities",
                        )}
                    /> 

                    <SidebarTab
                        route="MBSItems"
                        title="MBS Items"
                        iFont="fa-solid fa-m"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "MBSItems")}
                    />

                    <SidebarTab
                        route="ASAItems"
                        title="ASA Items"
                        iFont="fa-solid fa-a"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "ASAItems")}
                    />
                </ExpandableTab>

                <ExpandableTab
                    title="System"
                    iFont="fa fa-toolbox"
                    {sidebarExpanded}
                    isShow={_checkIfShowCollapseTab([
                        "Admin",
                        "DefaultFilters",
                        "CalendarCategories",
                        "GlobalEvents",
                        "PublicHolidays",
                        "Announcements",
                        "CompanyDocuments",
                        "OrderProducts",
                        "PortalCustomisation",
                    ])}
                    routesArr={[
                        "Admin",
                        "DefaultFilters",
                        "CalendarCategories",
                        "GlobalEvents",
                        "PublicHolidays",
                        "Announcements",
                        "CompanyDocuments",
                        "OrderProducts",
                        "PortalCustomisation",
                    ]}
                >
                    <SidebarTab
                        route="Admin"
                        title="Admins"
                        iFont="far fa-user-circle"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Admin")}
                    />

                    <SidebarTab
                        route="DefaultFilters"
                        title="Pre-Defined Filters"
                        iFont="fa-brands fa-searchengin"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "DefaultFilters")}
                    />

                    <SidebarTab
                        route="PortalCustomisation"
                        title="Portal Customisation"
                        iFont="fa-solid fa-sliders"
                        {sidebarExpanded}
                        isShow={checkIfShowTab(
                            $user.role,
                            "PortalCustomisation",
                        )}
                    />

                    <SidebarTab
                        route="CalendarCategories"
                        title="Calendar Categories"
                        iFont="fa fa-list-alt"
                        {sidebarExpanded}
                        isShow={checkIfShowTab(
                            $user.role,
                            "CalendarCategories",
                        )}
                    />

                    <SidebarTab
                        route="GlobalEvents"
                        title="Global Events"
                        iFont="fa-solid fa-globe"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "GlobalEvents")}
                    />

                    <SidebarTab
                        route="PublicHolidays"
                        title="Public Holidays"
                        iFont="fa fa-menorah"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "PublicHolidays")}
                    />

                    <SidebarTab
                        route="Announcements"
                        title="Announcements"
                        iFont="fa-solid fa-bullhorn"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "Announcements")}
                    />

                    <SidebarTab
                        route="CompanyDocuments"
                        title="Company Documents"
                        iFont="fa-solid fa-file"
                        {sidebarExpanded}
                        isShow={!checkEnvIsGroup($user.envSettings) && checkIfShowTab($user.role, "CompanyDocuments")} 
                    />

                    <SidebarTab
                        route="OrderProducts"
                        title="Products"
                        iFont="fa-solid fa-cart-shopping"
                        {sidebarExpanded}
                        isShow={!checkEnvIsGroup($user.envSettings) && checkIfShowTab($user.role, "CompanyDocuments")}
                    />

                </ExpandableTab>

                <ExpandableTab
                    title="Templates"
                    iFont="fa fa-file-pen"
                    {sidebarExpanded}
                    isShow={_checkIfShowCollapseTab([
                        "PreOpInstructions",
                        "PreOpQuestionnaires",
                        "EmailTemplates",
                        "SmsTemplates",
                        "PdfTemplates",
                    ])}
                    routesArr={[
                        "PreOpInstructions",
                        "PreOpQuestionnaires",
                        "EmailTemplates",
                        "SmsTemplates",
                        "PdfTemplates",
                    ]}
                >
                    <SidebarTab
                        route="PreOpInstructions"
                        title="PreOp Instructions"
                        iFont="fa-solid fa-sheet-plastic"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "PreOpInstructions")}
                    />

                    <SidebarTab
                        route="PreOpQuestionnaires"
                        title="PreOp Questionnaires"
                        iFont="fa-solid fa-clipboard"
                        {sidebarExpanded}
                        isShow={checkIfShowTab(
                            $user.role,
                            "PreOpQuestionnaires",
                        )}
                    />

                    <SidebarTab
                        route="EmailTemplates"
                        title="Email Templates"
                        iFont="fa fa-envelope"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "EmailTemplates")}
                    />

                    <SidebarTab
                        route="SmsTemplates"
                        title="Sms Templates"
                        iFont="fa fa-comment"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "SmsTemplates")}
                    />

                    <SidebarTab
                        route="PdfTemplates"
                        title="PDF Templates"
                        iFont="fa fa-file-pdf"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "PdfTemplates")}
                    />
                </ExpandableTab>

                <ExpandableTab
                    title="Logs"
                    iFont="fa fa-folder-closed"
                    {sidebarExpanded}
                    isShow={_checkIfShowCollapseTab([
                        "ActivityLogs",
                        "EclipseLogs",
                        "LoginLogs",
                        "UnauthorisedLogs",
                        "ExceptionLogs",
                    ])}
                    routesArr={[
                        "ActivityLogs",
                        "EclipseLogs",
                        "LoginLogs",
                        "UnauthorisedLogs",
                        "ExceptionLogs",
                    ]}
                >
                    <SidebarTab
                        route="ActivityLogs"
                        title="Activity Logs"
                        iFont="fa fa-folder-closed"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "ActivityLogs")}
                    />

                    <SidebarTab
                        route="EclipseLogs"
                        title="Eclipse Logs"
                        iFont="fa fa-folder-closed"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "EclipseLogs")}
                    />

                    <SidebarTab
                        route="LoginLogs"
                        title="Login Logs"
                        iFont="fa fa-folder-closed"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "LoginLogs")}
                    />

                    <SidebarTab
                        route="UnauthorisedLogs"
                        title="Unauthorised Logs"
                        iFont="fa fa-folder-closed"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "UnauthorisedLogs")}
                    />

                    <SidebarTab
                        route="ExceptionLogs"
                        title="Exception Logs"
                        iFont="fa fa-folder-closed"
                        {sidebarExpanded}
                        isShow={checkIfShowTab($user.role, "ExceptionLogs")}
                    />
                </ExpandableTab>
            </ul>
        </div>
    </div>
</div>

<style>
    .rotate-back {
        transform: rotate(0deg);
        transition: 0.2s linear;
    }

    .rotate-180 {
        transform: rotate(180deg);
        transition: 0.2s linear;
    }

    .sidebar {
        transition: 0.2s linear;
    }
</style>
